import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePostShippingLabelsParams, TypePostShippingLabelsResponse } from '~/types/delivery-agency.types';

/**
 * [운송장 관리] 운송장 생성 API
 */
const fetchPostShippingLabels = async (
  params: TypePostShippingLabelsParams
): Promise<TypePostShippingLabelsResponse['data']> => {
  const { data } = await axiosInstance.post<TypePostShippingLabelsResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.POST_SHIPPING_LABELS_BATCH,
    params
  );
  return data.data;
};

/**
 * [운송장 관리] 운송장 생성 Mutation Hook
 */
export const usePostShippingLabelsMutation = () => {
  return useMutation<
    TypePostShippingLabelsResponse['data'],
    TypeErrorResponseData['error'],
    TypePostShippingLabelsParams
  >({
    mutationFn: fetchPostShippingLabels,
    meta: { showGlobalErrorToast: false },
  });
};

/**
 * [운송장 관리] 추가 운송장 생성 Mock
 */
registerMockAPI<TypePostShippingLabelsResponse | TypeErrorResponseData>({
  method: 'post',
  endpoint: '/api-delivery-agency/shipping-labels/batch',
  label: '운송장 관리/추가 운송장 생성',
  scenarios: [
    {
      name: '정상 생성 (3개)',
      response: {
        data: [
          {
            shippingLabelId: 90001,
            shippingLabelNumber: '250-S0-26617T0002272-0002',
            outboundCode: '26617T0002272',
            receiverName: '김보리',
            productName: '[에스티 로더] 갈색병 세럼 50ml 기획세트 1+1 사은품 증정',
            createdAt: '2026-07-22T14:00:00',
          },
          {
            shippingLabelId: 90002,
            shippingLabelNumber: '250-S0-26617T0002272-0003',
            outboundCode: '26617T0002272',
            receiverName: '김보리',
            productName: '[에스티 로더] 갈색병 세럼 50ml 기획세트 1+1 사은품 증정',
            createdAt: '2026-07-22T14:00:01',
          },
          {
            shippingLabelId: 90003,
            shippingLabelNumber: '250-S0-26617T0002272-0004',
            outboundCode: '26617T0002272',
            receiverName: '김보리',
            productName: '[에스티 로더] 갈색병 세럼 50ml 기획세트 1+1 사은품 증정',
            createdAt: '2026-07-22T14:00:02',
          },
        ],
      },
    },
    {
      name: '도메인 에러 (다이얼로그)',
      status: 400,
      response: { error: { code: 61000, message: '해당 출고번호는 추가 운송장 생성이 불가합니다.' } },
    },
    {
      name: '서버 에러',
      status: 500,
      response: { error: { code: 50000, message: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.' } },
    },
  ],
});
